import { Box, Flex, Switch, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getDimensionLoaderStatus,
  getDimensionProperty,
  getLoaderStatus,
  getSelectedDimension,
  handleSettingsSection,
  UpdateDimensionName,
} from 'redux/BuilderModeSlice';
import SettingIconComponent from 'components/Icons/FigmaSettingIcon';

import { DimensionsSettingsSkeleton } from './DimensionsSkeletons';
import { ROBOTO, fieldLabelStyle, PanelSelect, PanelShell } from './PanelUi';
import { accent, neutral, radius, text as textToken } from './designTokens';

// Purple track to match the module's accent, instead of the default teal
// colorScheme the global Switch theme falls back to.
const switchSx = {
  '.chakra-switch__track[data-checked]': { background: accent.solid },
  '.chakra-switch__track:focus-visible, .chakra-switch__track[data-focus]': { boxShadow: 'none' },
};

const SettingToggleRow = ({
  label,
  isChecked,
  onChange,
}: {
  label: string;
  isChecked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Flex
    alignItems='center'
    justifyContent='space-between'
    gap='12px'
    bg='#F9F9FB'
    border={`1px solid ${neutral.borderSubtle}`}
    borderRadius={radius.control}
    px='12px'
    py='8px'
  >
    <Text fontSize='13px' fontFamily={ROBOTO} color={textToken.primary} lineHeight='1.35'>
      {label}
    </Text>
    <Switch
      size='sm'
      flexShrink={0}
      isChecked={Boolean(isChecked)}
      onChange={(event: any) => onChange(event.target.checked)}
      sx={switchSx}
    />
  </Flex>
);

const BuilderSettingComponent = () => {
  const selectedDimension: any = useSelector(getSelectedDimension);
  const dimensionProperty: any = useSelector(getDimensionProperty);
  const dimensionLoader = useSelector(getDimensionLoaderStatus);
  const loaderStatus = useSelector(getLoaderStatus);
  const dispatch: any = useDispatch();

  const handleClose = () => dispatch(handleSettingsSection());

  const handleSettingsChange = async (value: any, type: string) => {
    await dispatch(
      UpdateDimensionName({
        item_id: selectedDimension?.id,
        request_body: {
          [type]: `${value}`,
        },
      }),
    );
  };

  const groupOptions = (dimensionProperty || [])
    .filter((data: any) => data?.type === 'dimension')
    .map((data: any) => ({ value: String(data?.id), label: data?.name }));

  return (
    <PanelShell
      icon={<SettingIconComponent width={15} height={15} color={textToken.secondary} />}
      title='Settings'
      onClose={handleClose}
    >
      {dimensionLoader || loaderStatus ? (
        <DimensionsSettingsSkeleton />
      ) : (
        <Flex flexDir='column' gap='12px' px='12px' py='12px' overflowY='auto' className='scroll'>
          <SettingToggleRow
            label='Enable start and end dates'
            isChecked={selectedDimension?.enable_start_end}
            onChange={(checked) => handleSettingsChange(checked, 'enable_start_end')}
          />
          <SettingToggleRow
            label='Display dimension as driver page'
            isChecked={selectedDimension?.driver_page}
            onChange={(checked) => handleSettingsChange(checked, 'driver_page')}
          />
          <Box>
            <Text {...fieldLabelStyle}>Grouped by</Text>
            <PanelSelect
              value={selectedDimension?.group_property_id}
              placeholder='Select'
              options={groupOptions}
              onChange={(next) => handleSettingsChange(next, 'group_property_id')}
            />
            {groupOptions.length === 0 && (
              <Text
                fontSize='12px'
                fontFamily={ROBOTO}
                color={textToken.secondary}
                mt='6px'
                lineHeight='1.4'
              >
                Add a Dimension-type property to group items by.
              </Text>
            )}
          </Box>
        </Flex>
      )}
    </PanelShell>
  );
};
export default BuilderSettingComponent;
